import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Mail, Plus, Trash2, Send, Loader2, Bell, CheckCircle2, XCircle, ThermometerSnowflake, Snowflake, Package, Eye } from 'lucide-react';

export default function NotificationsPage() {
  const { api } = useAuth();
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [cfgRes, logsRes] = await Promise.all([
        api.get('/notifications/config'),
        api.get('/notifications/log').catch(() => ({ data: [] })),
      ]);
      setConfig(cfgRes.data);
      setLogs(logsRes.data);
    } catch { toast.error('Erreur chargement config'); }
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        recipients: config.recipients || [],
        alert_inventaire: !!config.alert_inventaire,
        alert_temperatures: !!config.alert_temperatures,
        alert_decongelation: !!config.alert_decongelation,
        hour_utc: config.hour_utc ?? 8,
      };
      const res = await api.put('/notifications/config', payload);
      setConfig(res.data);
      toast.success('Configuration enregistrée');
    } catch (e) { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const addEmail = () => {
    const e = newEmail.trim().toLowerCase();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error('Email invalide'); return; }
    if ((config.recipients || []).includes(e)) { toast.warning('Email déjà ajouté'); return; }
    setConfig(p => ({ ...p, recipients: [...(p.recipients || []), e] }));
    setNewEmail('');
  };

  const removeEmail = (em) => {
    setConfig(p => ({ ...p, recipients: (p.recipients || []).filter(x => x !== em) }));
  };

  const sendTest = async () => {
    if (!(config.recipients || []).length) { toast.error('Ajoute au moins un destinataire'); return; }
    setTesting(true);
    try {
      const res = await api.post('/notifications/test-email');
      if (res.data.status === 'sent') toast.success('Email test envoyé ✓');
      else if (res.data.status === 'skipped') toast.warning(`Skip : ${res.data.error}`);
      else toast.error(res.data.error || 'Échec envoi');
      fetchAll();
    } catch (e) { toast.error('Erreur envoi test : ' + (e.response?.data?.detail || e.message)); }
    finally { setTesting(false); }
  };

  const runNow = async (force = false) => {
    setRunning(true);
    try {
      const res = await api.post('/notifications/run-daily-check', null, { params: { force } });
      const d = res.data;
      if (d.status === 'sent') toast.success(`Digest envoyé (${d.digest.manquements.length} manquement(s))`);
      else if (d.status === 'ok') toast.info(d.message);
      else if (d.status === 'skipped') toast.warning(d.send_result?.error || 'Skip');
      else toast.error(d.send_result?.error || 'Erreur');
      fetchAll();
    } catch (e) { toast.error('Erreur : ' + (e.response?.data?.detail || e.message)); }
    finally { setRunning(false); }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await api.get('/notifications/preview-digest');
      setPreview(res.data);
    } catch { toast.error('Erreur preview'); }
    finally { setPreviewLoading(false); }
  };

  if (!config) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6 p-6 max-w-6xl mx-auto" data-testid="notifications-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center"><Bell className="w-5 h-5 text-red-600" /></div>
        <div>
          <h1 className="text-2xl font-bold">Alertes & Notifications</h1>
          <p className="text-sm text-muted-foreground">Reçois un mail tous les matins si les vérifs HACCP n'ont pas été faites la veille</p>
        </div>
      </div>

      {/* Destinataires */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5" /> Destinataires</CardTitle>
          <CardDescription>Adresses qui recevront le digest quotidien</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="email@example.com"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addEmail()}
              data-testid="notif-new-email"
            />
            <Button onClick={addEmail} data-testid="notif-add-email"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
          </div>
          {(config.recipients || []).length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Aucun destinataire — aucune alerte ne sera envoyée.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {config.recipients.map(em => (
                <Badge key={em} variant="secondary" className="text-sm px-3 py-1 flex items-center gap-2">
                  {em}
                  <button onClick={() => removeEmail(em)} className="text-red-600 hover:text-red-800" data-testid={`notif-remove-${em}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modules surveillés */}
      <Card>
        <CardHeader>
          <CardTitle>Modules surveillés</CardTitle>
          <CardDescription>L'alerte se déclenche si AUCUNE action n'a été enregistrée la veille pour le module activé</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-md border">
            <div className="flex items-center gap-3"><Package className="w-5 h-5 text-blue-600" /><div><div className="font-semibold">Inventaire</div><div className="text-xs text-muted-foreground">Au moins 1 modification de stock / création / suppression</div></div></div>
            <Switch checked={config.alert_inventaire} onCheckedChange={v => setConfig(p => ({ ...p, alert_inventaire: v }))} data-testid="alert-inventaire" />
          </div>
          <div className="flex items-center justify-between p-3 rounded-md border">
            <div className="flex items-center gap-3"><ThermometerSnowflake className="w-5 h-5 text-cyan-600" /><div><div className="font-semibold">Relevés de températures</div><div className="text-xs text-muted-foreground">Au moins 1 relevé frigo ou produit</div></div></div>
            <Switch checked={config.alert_temperatures} onCheckedChange={v => setConfig(p => ({ ...p, alert_temperatures: v }))} data-testid="alert-temperatures" />
          </div>
          <div className="flex items-center justify-between p-3 rounded-md border">
            <div className="flex items-center gap-3"><Snowflake className="w-5 h-5 text-indigo-600" /><div><div className="font-semibold">Décongélation</div><div className="text-xs text-muted-foreground">Au moins 1 produit décongelé enregistré</div></div></div>
            <Switch checked={config.alert_decongelation} onCheckedChange={v => setConfig(p => ({ ...p, alert_decongelation: v }))} data-testid="alert-decongelation" />
          </div>

          <div className="flex items-center gap-3">
            <Label htmlFor="hour">Heure d'envoi (UTC)</Label>
            <Input id="hour" type="number" min={0} max={23} className="w-24" value={config.hour_utc ?? 8} onChange={e => setConfig(p => ({ ...p, hour_utc: parseInt(e.target.value) || 0 }))} data-testid="alert-hour" />
            <span className="text-xs text-muted-foreground">Paris = UTC+1 hiver / UTC+2 été (mets 8 pour ~9-10h locale)</span>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={save} disabled={saving} data-testid="notif-save">
          {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Enregistrer la config
        </Button>
        <Button variant="outline" onClick={sendTest} disabled={testing} data-testid="notif-send-test">
          {testing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Envoyer email test
        </Button>
        <Button variant="outline" onClick={() => runNow(false)} disabled={running} data-testid="notif-run-now">
          {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Bell className="w-4 h-4 mr-1" />} Lancer le check d'hier
        </Button>
        <Button variant="outline" onClick={() => runNow(true)} disabled={running} data-testid="notif-force">
          Forcer envoi (même sans manquement)
        </Button>
        <Button variant="ghost" onClick={loadPreview} disabled={previewLoading} data-testid="notif-preview">
          {previewLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Eye className="w-4 h-4 mr-1" />} Aperçu du mail
        </Button>
      </div>

      {/* Preview */}
      {preview && (
        <Card>
          <CardHeader><CardTitle className="text-base">Aperçu pour le {preview.target_date}</CardTitle></CardHeader>
          <CardContent>
            <div className="border rounded-md overflow-hidden bg-white" dangerouslySetInnerHTML={{ __html: preview.html }} />
          </CardContent>
        </Card>
      )}

      {/* Logs */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5" /> Historique d'envoi</CardTitle></CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Aucun email envoyé pour l'instant.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Cible</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Manquements</TableHead>
                  <TableHead>Destinataires</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs">{new Date(log.datetime).toLocaleString('fr-FR')}</TableCell>
                    <TableCell className="text-xs">{log.target_date}</TableCell>
                    <TableCell>
                      {log.send_result?.status === 'sent'
                        ? <Badge className="bg-green-100 text-green-700 border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" /> Envoyé</Badge>
                        : <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> {log.send_result?.status}</Badge>}
                    </TableCell>
                    <TableCell className="text-xs">{(log.manquements || []).join(', ') || '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{(log.recipients || []).join(', ')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
